import { useState, useCallback } from 'react';
import { Link, router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';
import { AppShell, PageHeader } from '../../../components/layout';
import { Button, Card } from '../../../components/ui';
import { ShortAnswer } from '../../../components/questions';

export default function PracticeExercises({
  student,
  session,
  exercises,
  skill_focus,
}) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [feedback, setFeedback] = useState({});
  const [isChecking, setIsChecking] = useState(false);

  const currentExercise = exercises[currentIndex];
  const totalExercises = exercises.length;

  const handleResponseChange = useCallback((exerciseId, value) => {
    setResponses((prev) => ({
      ...prev,
      [exerciseId]: value,
    }));
  }, []);

  const handleCheckAnswer = useCallback(async () => {
    setIsChecking(true);
    // Simulate AI feedback delay
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Mock feedback
    setFeedback((prev) => ({
      ...prev,
      [currentExercise.id]: {
        isCorrect: true,
        message: "Good work! You correctly identified specific evidence from the passage.",
      },
    }));
    setIsChecking(false);
  }, [currentExercise]);

  const handleNextExercise = useCallback(() => {
    if (currentIndex < totalExercises - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // All exercises complete
      router.visit('/');
    }
  }, [currentIndex, totalExercises]);

  const exerciseFeedback = feedback[currentExercise?.id];
  const currentResponse = responses[currentExercise?.id] || '';
  const canCheck = currentResponse.trim().length > 0 && !exerciseFeedback;

  return (
    <AppShell student={student} maxWidth="narrow" scrollTitle={<><Link href={`/student/practice/${session.id}/results`} className="hover:text-blue-600 dark:hover:text-blue-400">{t('practice.backToResults')}</Link>{'  ›  '}{skill_focus}</>}>
      <PageHeader
        backLink={`/student/practice/${session.id}/results`}
        backLabel={t('practice.backToResults')}
      />

      {/* Header Card */}
      <Card gradient className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Practice: {skill_focus}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {currentExercise?.introduction ||
            t('practice.exerciseIntro')}
        </p>
      </Card>

      {/* Progress */}
      <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {t('practice.exercise')} {currentIndex + 1} {t('common.of')} {totalExercises}
      </div>

      {/* Exercise Card */}
      {currentExercise && (
        <Card className="mb-6">
          {/* Passage */}
          <div className="mb-6">
            <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              {t('practice.readPassage')}
            </h3>
            <blockquote className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-gray-700 dark:text-gray-300 leading-relaxed border-l-4 border-gray-300 dark:border-gray-600">
              {currentExercise.passage}
            </blockquote>
          </div>

          {/* Question */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {currentExercise.prompt}
            </h3>
            {currentExercise.guidance && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {currentExercise.guidance}
              </p>
            )}
            <ShortAnswer
              value={currentResponse}
              onChange={(value) => handleResponseChange(currentExercise.id, value)}
              placeholder={t('practice.typeAnswerHere')}
              disabled={!!exerciseFeedback}
            />
          </div>

          {/* Feedback */}
          {exerciseFeedback && (
            <div
              className={cn(
                'rounded-lg p-4 mb-6',
                exerciseFeedback.isCorrect
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
              )}
            >
              <div className="flex items-start gap-3">
                <span className="text-xl">
                  {exerciseFeedback.isCorrect ? '✓' : '↻'}
                </span>
                <p
                  className={cn(
                    'text-sm',
                    exerciseFeedback.isCorrect ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'
                  )}
                >
                  {exerciseFeedback.message}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            {!exerciseFeedback ? (
              <Button
                variant="primary"
                onClick={handleCheckAnswer}
                disabled={!canCheck || isChecking}
              >
                {isChecking ? (
                  <>
                    <span className="inline-flex items-center gap-1 mr-2">
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce [animation-delay:-0.3s]" />
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce [animation-delay:-0.15s]" />
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" />
                    </span>
                    {t('practice.checking')}
                  </>
                ) : (
                  <>
                    {t('practice.checkAnswer')}
                    <svg
                      className="ml-2 w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </>
                )}
              </Button>
            ) : (
              <Button variant="primary" onClick={handleNextExercise}>
                {currentIndex === totalExercises - 1 ? t('practice.finish') : t('practice.nextExercise')}
                <svg
                  className="ml-2 w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Progress Indicator */}
      <div className="flex justify-center gap-2">
        {exercises.map((_, index) => (
          <span
            key={index}
            className={cn(
              'w-2 h-2 rounded-full transition-all duration-200',
              index === currentIndex
                ? 'bg-gray-900 dark:bg-white w-4'
                : index < currentIndex
                ? 'bg-green-500 dark:bg-green-400'
                : 'bg-gray-300 dark:bg-gray-600'
            )}
          />
        ))}
      </div>
    </AppShell>
  );
}
